import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

export interface UseArtBrowsingNudgeOptions {
  threshold?: number;
  autoDismissSeconds?: number;
  maxLifetimeShows?: number;
}

const LOCAL_KEY_LIFETIME_COUNT = "qam_selections_nudge_lifetime_count";
const LOCAL_KEY_INTERACTED = "qam_artworks_interacted";
const SESSION_KEY_SHOWN = "qam_selections_nudge_shown";
const SESSION_KEY_DISMISSED = "qam_selections_nudge_dismissed";
const SESSION_KEY_INTERACTED = "qam_artworks_interacted";

const DEFAULT_MAX_LIFETIME_SHOWS = 3;

// Shared singleton state so Navbar and Artworks page stay in sync
const viewedArtworkIds = ref<Set<string | number>>(new Set());
const isNudgeActive = ref(false);
const hasInteracted = ref(false);
const isDismissed = ref(false);
const hasAlreadyShownInSession = ref(false);
const lifetimeShownCount = ref(0);
const isInitialized = ref(false);
const remainingSeconds = ref(10);

let isDatabaseSyncing = false;
let isDatabaseSynced = false;
let lastSyncedUserId: string | null = null;

let observer: IntersectionObserver | null = null;
const observedElements = new Map<Element, string | number>();
let countdownTimer: ReturnType<typeof setInterval> | null = null;

function startAutoDismissCountdown(seconds = 10) {
  stopAutoDismissCountdown();
  remainingSeconds.value = seconds;
  if (!import.meta.client) return;
  countdownTimer = setInterval(() => {
    if (remainingSeconds.value > 1) {
      remainingSeconds.value--;
    } else {
      remainingSeconds.value = 0;
      stopAutoDismissCountdown();
      dismissNudgeInternal();
    }
  }, 1000);
}

function stopAutoDismissCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
}

function dismissNudgeInternal() {
  stopAutoDismissCountdown();
  isNudgeActive.value = false;
  isDismissed.value = true;
  if (import.meta.client) {
    try {
      sessionStorage.setItem(SESSION_KEY_DISMISSED, "true");
    } catch {}
  }
}

export function resetNudgeState() {
  stopAutoDismissCountdown();
  isNudgeActive.value = false;
  hasInteracted.value = false;
  isDismissed.value = false;
  hasAlreadyShownInSession.value = false;
  lifetimeShownCount.value = 0;
  viewedArtworkIds.value = new Set();
  isInitialized.value = false;
  isDatabaseSynced = false;
  isDatabaseSyncing = false;
  lastSyncedUserId = null;

  if (import.meta.client) {
    try {
      sessionStorage.removeItem(SESSION_KEY_SHOWN);
      sessionStorage.removeItem(SESSION_KEY_DISMISSED);
      sessionStorage.removeItem(SESSION_KEY_INTERACTED);
      localStorage.removeItem(LOCAL_KEY_LIFETIME_COUNT);
      localStorage.removeItem(LOCAL_KEY_INTERACTED);
    } catch {}
  }
}

export function useArtBrowsingNudge(options: UseArtBrowsingNudgeOptions = {}) {
  const threshold = options.threshold ?? 30;
  const autoDismissSeconds = options.autoDismissSeconds ?? 10;
  const maxLifetimeShows = options.maxLifetimeShows ?? DEFAULT_MAX_LIFETIME_SHOWS;
  const route = useRoute();
  const supabase = useSupabaseClient();
  const user = useSupabaseUser();

  const viewedCount = computed(() => viewedArtworkIds.value.size);

  const shouldShowNudge = computed(() => {
    if (route.path !== "/artworks") return false;
    if (hasInteracted.value || isDismissed.value) return false;
    if (lifetimeShownCount.value > maxLifetimeShows) return false;
    return isNudgeActive.value;
  });

  async function syncFromDatabase() {
    if (!user.value || user.value.is_anonymous || !import.meta.client) return;
    const userId = user.value.id || (user.value as any).sub;
    if (!userId) return;
    if (isDatabaseSynced && lastSyncedUserId === userId) return;
    if (isDatabaseSyncing) return;

    isDatabaseSyncing = true;
    try {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("nudge_shown_count")
        .eq("id", userId)
        .single();

      if (!error && data && typeof data.nudge_shown_count === "number") {
        lifetimeShownCount.value = Math.max(lifetimeShownCount.value, data.nudge_shown_count);
        try {
          localStorage.setItem(LOCAL_KEY_LIFETIME_COUNT, String(lifetimeShownCount.value));
        } catch {}
      }
      isDatabaseSynced = true;
      lastSyncedUserId = userId;
    } catch (err) {
      console.warn("[useArtBrowsingNudge] Error fetching count from profiles table:", err);
    } finally {
      isDatabaseSyncing = false;
    }
  }

  function initSessionState() {
    if (!import.meta.client) return;
    if (!isInitialized.value) {
      try {
        const storedLifetime = localStorage.getItem(LOCAL_KEY_LIFETIME_COUNT);
        if (storedLifetime) {
          lifetimeShownCount.value = parseInt(storedLifetime, 10) || 0;
        }
        if (
          localStorage.getItem(LOCAL_KEY_INTERACTED) === "true" ||
          sessionStorage.getItem(SESSION_KEY_INTERACTED) === "true"
        ) {
          hasInteracted.value = true;
        }
        if (sessionStorage.getItem(SESSION_KEY_DISMISSED) === "true") {
          isDismissed.value = true;
        }
        if (sessionStorage.getItem(SESSION_KEY_SHOWN) === "true") {
          hasAlreadyShownInSession.value = true;
        }
      } catch {
        // Ignore storage errors
      }
      isInitialized.value = true;
    }
    syncFromDatabase();
  }

  async function syncCountToSupabase() {
    if (!user.value || user.value.is_anonymous || !import.meta.client) return;
    const userId = user.value.id || (user.value as any).sub;
    if (!userId) return;

    try {
      // 1. Fetch current count from DB to never overwrite a higher count
      const { data } = await (supabase as any)
        .from("profiles")
        .select("nudge_shown_count")
        .eq("id", userId)
        .single();

      const currentDbCount = typeof data?.nudge_shown_count === "number" ? data.nudge_shown_count : 0;
      const finalCount = Math.max(currentDbCount + 1, lifetimeShownCount.value);
      lifetimeShownCount.value = finalCount;

      try {
        localStorage.setItem(LOCAL_KEY_LIFETIME_COUNT, String(finalCount));
      } catch {}

      // 2. Update profiles table with the incremented count
      await (supabase as any)
        .from("profiles")
        .update({ nudge_shown_count: finalCount })
        .eq("id", userId);
    } catch (err: any) {
      console.warn("[useArtBrowsingNudge] Error updating profiles table:", err);
    }
  }

  function syncInteractedToSupabase() {
    if (!user.value || user.value.is_anonymous) return;
    supabase.auth
      .updateUser({
        data: {
          nudge_interacted: true,
        },
      })
      .catch((err) => {
        console.warn("[useArtBrowsingNudge] Failed to sync interaction to Supabase auth:", err);
      });
  }

  function recordInteraction() {
    stopAutoDismissCountdown();
    isNudgeActive.value = false;
    hasInteracted.value = true;
    if (import.meta.client) {
      try {
        localStorage.setItem(LOCAL_KEY_INTERACTED, "true");
        sessionStorage.setItem(SESSION_KEY_INTERACTED, "true");
      } catch {}
    }
    syncInteractedToSupabase();
  }

  function dismissNudge() {
    dismissNudgeInternal();
  }

  function observeArtwork(el: Element | null, id: string | number | undefined) {
    if (!el || id === undefined || id === null) return;
    if (!import.meta.client || typeof IntersectionObserver === "undefined") return;

    if (viewedArtworkIds.value.has(id)) {
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          let updated = false;
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const artworkId = observedElements.get(entry.target);
              if (artworkId !== undefined && !viewedArtworkIds.value.has(artworkId)) {
                viewedArtworkIds.value.add(artworkId);
                updated = true;
              }
              observer?.unobserve(entry.target);
              observedElements.delete(entry.target);
            }
          }

          if (updated) {
            viewedArtworkIds.value = new Set(viewedArtworkIds.value);
            if (
              !hasInteracted.value &&
              !isDismissed.value &&
              !hasAlreadyShownInSession.value &&
              lifetimeShownCount.value < maxLifetimeShows &&
              viewedArtworkIds.value.size > threshold
            ) {
              isNudgeActive.value = true;
              hasAlreadyShownInSession.value = true;
              lifetimeShownCount.value++;

              if (import.meta.client) {
                try {
                  sessionStorage.setItem(SESSION_KEY_SHOWN, "true");
                  localStorage.setItem(LOCAL_KEY_LIFETIME_COUNT, String(lifetimeShownCount.value));
                } catch {}
              }

              syncCountToSupabase();
              startAutoDismissCountdown(autoDismissSeconds);
            }
          }
        },
        {
          rootMargin: "50px 0px 50px 0px",
          threshold: 0.15,
        }
      );
    }

    observedElements.set(el, id);
    observer.observe(el);
  }

  watch(
    () => user.value?.id,
    (newId, oldId) => {
      if (newId) {
        syncFromDatabase();
      } else if (oldId && !newId) {
        resetNudgeState();
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    initSessionState();
  });

  return {
    viewedArtworkIds,
    viewedCount,
    hasInteracted,
    isDismissed,
    hasAlreadyShownInSession,
    lifetimeShownCount,
    shouldShowNudge,
    remainingSeconds,
    recordInteraction,
    dismissNudge,
    resetNudgeState,
    observeArtwork,
  };
}
